import { useState, useMemo, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import ActionTitleFilter from "./ActionTitleFilter";
import PublicView from "./PublicView";
import SelectAddNoteType from "@/components/VenueDetailBasic/Feature/SelectAddNoteType";
import AreaDetailsNotes from "@/components/VenueDetailBasic/Feature/AreaDetailsNotes";
import AddMapperNote from "@/components/VenueDetailBasic/Feature/AddMapperNote";
import AddVenueNote from "@/components/VenueDetailBasic/Feature/AddVenueNote";
import EditVenueNote from "@/components/VenueDetailBasic/Feature/EditVenueNote";
import FlagVenueNote from "@/features/flag-report/FlagVenueNote";
import { cn } from "@/lib/utils";
import { BellIcon, FolderDottedIcon, PlusCircleIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { pinTypeForVenueDetails } from "common/utils";
import { StandardButton } from "@/components/common/StandardButton";
import { createPageAnalytics, trackEvent } from "common/analytics";
import NotificationEmptyState from "../NotificationEmptyState";

type VenueMapperNotesProps = {
  venueDetailsResponse: any;
  onRefresh: () => void;
  showMapperView: boolean;
  onEdit: (note: any) => void;
  onFlagNote: (note: any) => void;
  onNotificationClick: () => void;
  isNotificationEnabled: () => boolean;
};

const VenueMapperNotes = ({
  venueDetailsResponse,
  onRefresh,
  showMapperView,
  onEdit,
  onFlagNote,
  onNotificationClick,
  isNotificationEnabled,
}: VenueMapperNotesProps) => {
  // Get notes data
  const allBusinessNotes = useMemo(
    () => venueDetailsResponse.details.businessNotesAll || [],
    [venueDetailsResponse.details.businessNotesAll],
  );

  // Refs for scrolling to sections
  const noteSectionRefs = useRef(new Map());
  const isProgrammaticScroll = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // Set default to undefined - will be set by ActionTitleFilter
  const [selectedNoteType, setSelectedNoteType] = useState<
    "Mapper" | "Live Alerts" | "Notes" | undefined
  >(undefined);
  const [businessNotesSortOrder, setBusinessNotesSortOrder] = useState(1);

  // Modal state management
  const [addNoteTypeOpenModal, setAddNoteTypeOpenModal] = useState(false);
  const [addNoteOpenModal, setAddNoteOpenModal] = useState(false);
  const [addVenueNoteOpenModal, setAddVenueNoteOpenModal] = useState(false);
  const [editVenueNoteOpenModal, setEditVenueNoteOpenModal] = useState(false);
  const [flagVenueNoteOpenModal, setFlagVenueNoteOpenModal] = useState(false);
  const [editNote, setEditNote] = useState<any>(null);
  const [flagNote, setFlagNote] = useState<any>(null);
  const [areaDetailsOpenModal, setAreaDetailsOpenModal] = useState(false);
  const i18n = useI18N();

  // Handlers for edit and flag note
  const handleEditNote = (note: any) => {
    setEditNote(note);
    setEditVenueNoteOpenModal(true);
  };

  const handleFlagNote = (note: any) => {
    // alert_flag_cta_activated — the alert-flag CTA, fired BEFORE the flag form opens
    // (flag_alert_form_opened fires on the FlagVenueNote mount). The parent's `onFlagNote`
    // CTA is on a dead path (it opens a separate, unused modal), so the flag button here
    // never triggered it — fire it directly where the flag actually opens. Primitive, not
    // createPageAnalytics: the place.flag_action_button element is overloaded.
    const details = venueDetailsResponse?.details;
    trackEvent("alert_flag_cta_activated", {
      alert_title: note?.action_title ?? "",
      brand_id: details?.brandId ?? "",
      brand_name: details?.brandName ?? "",
      business_id: details?.business?.id != null ? String(details.business.id) : "",
      place_name: details?.name ?? "",
    });
    setFlagNote(note);
    setFlagVenueNoteOpenModal(true);
  };

  // Sort all notes (no filtering)
  const sortedAllNotes = useMemo(() => {
    const sortNotes = (notes: any[]) => {
      if (businessNotesSortOrder === 1) {
        return [...notes].sort((a, b) => {
          const dateA =
            a.business_note_text_id ||
            (a.note && a.note.business_note_text_id) ||
            (a.details && a.details.business_note_text_id);
          const dateB =
            b.business_note_text_id ||
            (b.note && b.note.business_note_text_id) ||
            (b.details && b.details.business_note_text_id);
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
      }
      if (businessNotesSortOrder === 2) {
        return [...notes].sort((a, b) => {
          const dateA =
            a.business_note_text_id ||
            (a.note && a.note.business_note_text_id) ||
            (a.details && a.details.business_note_text_id);
          const dateB =
            b.business_note_text_id ||
            (b.note && b.note.business_note_text_id) ||
            (b.details && b.details.business_note_text_id);
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        });
      }
      if (businessNotesSortOrder === 3) {
        return [...notes].sort((a, b) => {
          const aType =
            a?.location_type ||
            (a.note && a.note.location_type) ||
            (a.details && a.details.location_type) ||
            "";
          const bType =
            b?.location_type ||
            (b.note && b.note.location_type) ||
            (b.details && b.details.location_type) ||
            "";
          return aType.localeCompare(bType);
        });
      }
      return notes;
    };

    return sortNotes(allBusinessNotes);
  }, [allBusinessNotes, businessNotesSortOrder]);

  // Group notes by type
  const groupedNotes = useMemo(() => {
    const mapper = sortedAllNotes.filter(
      (note) => note.note_type === "Basic Mapper Note",
    );

    const liveAlerts = sortedAllNotes.filter((note) => {
      if (!note || typeof note !== "object") return false;
      if (note.note_type !== "Venue Note") return false;

      const actionTitle =
        note.action_title ||
        (note.note && note.note.action_title) ||
        (note.details && note.details.action_title);

      return actionTitle === "Live Alerts";
    });

    const notes = sortedAllNotes.filter((note) => {
      if (!note || typeof note !== "object") return false;
      if (note.note_type !== "Venue Note") return false;

      const actionTitle =
        note.action_title ||
        (note.note && note.note.action_title) ||
        (note.details && note.details.action_title);

      return actionTitle === "Notes" || !actionTitle;
    });

    return { mapper, liveAlerts, notes };
  }, [sortedAllNotes]);

  // Calculate counts for each tab
  const mapperNotesCount = useMemo(
    () => groupedNotes.mapper.length,
    [groupedNotes.mapper],
  );

  // Scroll to section when tab is clicked
  const scrollToSection = (sectionName: string) => {
    const sectionRef = noteSectionRefs.current.get(sectionName);
    if (sectionRef) {
      isProgrammaticScroll.current = true;
      sectionRef.scrollIntoView({ behavior: "smooth", block: "start" });
      // Reset flag after scroll completes
      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 1000);
    }
  };

  // Set first tab as selected by default when notes are loaded
  useEffect(() => {
    if (!selectedNoteType) {
      if (showMapperView && mapperNotesCount > 0) {
        setSelectedNoteType("Mapper");
      } else if (groupedNotes.liveAlerts.length > 0) {
        setSelectedNoteType("Live Alerts");
      } else if (groupedNotes.notes.length > 0) {
        setSelectedNoteType("Notes");
      } else {
        setSelectedNoteType("Live Alerts"); // Default fallback
      }
    }
  }, [showMapperView, mapperNotesCount, groupedNotes, selectedNoteType]);

  // Scroll spy - auto-select tab when scrolling through sections
  useEffect(() => {
    if (sortedAllNotes.length === 0) return;

    // Find the scrollable parent container
    const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;
      
      let current = element.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        
        if (overflowY === 'auto' || overflowY === 'scroll' || 
            overflowX === 'auto' || overflowX === 'scroll') {
          return current;
        }
        
        current = current.parentElement;
      }
      
      return null;
    };

    const scrollContainer = containerRef.current 
      ? findScrollableParent(containerRef.current) 
      : null;
    
    if (!scrollContainer) {
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Don't update during programmatic scrolls
      if (isProgrammaticScroll.current) {
        return;
      }

      // Debounce scroll updates to prevent jitter
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const navHeight = navRef.current ? navRef.current.offsetHeight : 64;
        const viewportHeight = window.innerHeight;
        let activeSection: "Mapper" | "Live Alerts" | "Notes" | "" = "";
        let lastAboveNav: "Mapper" | "Live Alerts" | "Notes" | "" = "";

        for (const [sectionName, ref] of noteSectionRefs.current) {
          if (!ref) continue;
          const rect = ref.getBoundingClientRect();
          if (rect.top < navHeight) {
            lastAboveNav = sectionName as "Mapper" | "Live Alerts" | "Notes";
          } else if (rect.top < viewportHeight) {
            activeSection = sectionName as "Mapper" | "Live Alerts" | "Notes";
            break;
          } else {
            break;
          }
        }

        if (!activeSection) activeSection = lastAboveNav;
        if (activeSection) setSelectedNoteType(activeSection);
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [sortedAllNotes]);

  
  const placeAnalytics = createPageAnalytics("place");
  // Venue context for PublicView → VenueNoteView resolve-alert dialog events.
  const resolveBusinessId =
    venueDetailsResponse?.details?.business?.id?.toString() ?? "";
  const resolvePlaceName = venueDetailsResponse?.details?.name ?? "";
  const resolveBrandId = venueDetailsResponse?.details?.brandId ?? "";
  const resolveBrandName = venueDetailsResponse?.details?.brandName ?? "";

  const handleAlertNotificationClickWithTracking = () => {
    placeAnalytics.track("subscribe_live_alerts_action_button", {
      brand_id: venueDetailsResponse?.details?.brandId ?? "",
      brand_name: venueDetailsResponse?.details?.brandName ?? "",
      business_id: venueDetailsResponse?.details?.business?.id?.toString() ?? "",
      pin_type: pinTypeForVenueDetails(venueDetailsResponse?.details),
      place_name: venueDetailsResponse?.details?.name ?? "",
    });

    // Always open the modal
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  // Opens the "select note type" menu (SelectAddNoteType = add_note_menu). Fires the place CTA
  // event + the menu-opened event here, since SelectAddNoteType has no venue context.
  const handleOpenAddNote = () => {
    const businessId =
      venueDetailsResponse?.details?.business?.id?.toString() ?? "";
    const placeName = venueDetailsResponse?.details?.name ?? "";
    const brandId = venueDetailsResponse?.details?.brandId ?? "";
    const brandName = venueDetailsResponse?.details?.brandName ?? "";
    placeAnalytics.track("report_alerts_action_button", {
      brand_id: brandId,
      brand_name: brandName,
      business_id: businessId,
      pin_type: pinTypeForVenueDetails(venueDetailsResponse?.details),
      place_name: placeName,
    });
    createPageAnalytics("add_note_menu").track("page_view_page", {
      brand_id: brandId,
      brand_name: brandName,
      business_id: businessId,
      place_name: placeName,
    });
    setAddNoteTypeOpenModal(true);
  };

  return (
    <>
      {/* Scroll spy live announcement for screen readers */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {selectedNoteType ? `${selectedNoteType} section` : ""}
      </div>

      <Helmet>
        <title>
          {venueDetailsResponse?.details?.name
            ? `${venueDetailsResponse.details.name.length > 30 ? venueDetailsResponse.details.name.substring(0, 30) + "..." : venueDetailsResponse.details.name}`
            : ""}
          | Alerts | Sociability
        </title>
      </Helmet>

      {sortedAllNotes.length === 0 && (
        <div className="flex flex-col w-full min-h-[calc(100vh-200px)]">
          {!isNotificationEnabled() && (
            <div className="flex flex-1 flex-col justify-start sm:justify-center items-center px-4 pt-8 sm:pt-0">
              <FolderDottedIcon size={32} className="text-fg-neutral-strong" />
              <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
                {i18n.get("noAlertsReportedYet")}
              </p>
              <p className="text-center label-s-regular text-fg-neutral-subtle mt-2 max-w-sm">
                {i18n.get("tapSubscribeForLiveAlertsToGetAccessibilityUpdates")}
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                <StandardButton
                  variant="venueNew"
                  onClick={() => {
                    handleAlertNotificationClickWithTracking();
                  }}
                  className="w-full sm:w-auto flex-shrink-0"
                >
                  <BellIcon size={20} className="text-fg-neutral-strong" />
                  <span className="label-m-medium">{i18n.get("subscribeForLiveAlerts")}</span>
                </StandardButton>

                {/* Add Alert button */}
                {showMapperView && (
                  <StandardButton
                    variant="venueNew"
                    onClick={handleOpenAddNote}
                    className="w-full sm:w-auto flex-shrink-0"
                  >
                    <PlusCircleIcon size={20} className="text-fg-neutral-strong" />
                    <span className="label-m-medium">{i18n.get("addAlert")}</span>
                  </StandardButton>
                )}
              </div>
            </div>
          )}

          {isNotificationEnabled() && (
            <div className="flex flex-1 flex-col justify-start sm:justify-center items-center pt-8 sm:pt-0">
              <NotificationEmptyState
                onManageNotifications={handleAlertNotificationClickWithTracking}
              />
            </div>
          )}
        </div>
      )}

      {sortedAllNotes.length > 0 && (
        <div className="flex flex-col w-full">
          {/* Navigation with tabs */}
          <nav
            ref={navRef}
            className="sticky top-0 z-10 bg-bg-brand-secondary-default -mx-4 px-4 md:mx-0 md:px-0"
            aria-label="Alerts categories"
          >
            <div className="border-b border-b-primary-150">
              <ActionTitleFilter
                selectedNoteType={selectedNoteType}
                setSelectedNoteType={setSelectedNoteType}
                mapperNotesCount={mapperNotesCount}
                showMapperView={showMapperView}
                onSortChange={setBusinessNotesSortOrder}
                venueNotes={allBusinessNotes}
                showAddButton={showMapperView}
                onAddClick={handleOpenAddNote}
                onScrollToSection={scrollToSection}
                liveAlertsCount={groupedNotes.liveAlerts.length}
                notesCount={groupedNotes.notes.length}
              />
            </div>
          </nav>

          {/* Content Area */}
          <div ref={containerRef} className="flex flex-col pt-3 pb-[100vh] w-full min-w-0">
            {/* Mapper Notes Section */}
            {showMapperView && groupedNotes.mapper.length > 0 && (
              <div
                ref={(ref) => {
                  if (ref) {
                    noteSectionRefs.current.set("Mapper", ref);
                  }
                }}
                className="scroll-mt-52"
              >
                <PublicView
                  allBusinessNotes={groupedNotes.mapper}
                  onRefresh={onRefresh}
                  onEdit={handleEditNote}
                  onFlagNote={handleFlagNote}
                  showMapperView={showMapperView}
                  businessNotesActionsVenueNotes={
                    venueDetailsResponse.details
                      .businessNotesActionsVenueNotes
                  }
                  businessNotesActions={
                    venueDetailsResponse.details.businessNotesActions
                  }
                  businessId={resolveBusinessId}
                  placeName={resolvePlaceName}
                  brandId={resolveBrandId}
                  brandName={resolveBrandName}
                  isFirstSection={true}
                />
              </div>
            )}

            {/* Live Alerts Section */}
            {groupedNotes.liveAlerts.length > 0 && (
              <div
                ref={(ref) => {
                  if (ref) {
                    noteSectionRefs.current.set("Live Alerts", ref);
                  }
                }}
                className="scroll-mt-52"
              >
                <PublicView
                  allBusinessNotes={groupedNotes.liveAlerts}
                  onRefresh={onRefresh}
                  onEdit={handleEditNote}
                  onFlagNote={handleFlagNote}
                  showMapperView={showMapperView}
                  businessNotesActionsVenueNotes={
                    venueDetailsResponse.details
                      .businessNotesActionsVenueNotes
                  }
                  businessNotesActions={
                    venueDetailsResponse.details.businessNotesActions
                  }
                  businessId={resolveBusinessId}
                  placeName={resolvePlaceName}
                  brandId={resolveBrandId}
                  brandName={resolveBrandName}
                  isFirstSection={!showMapperView || groupedNotes.mapper.length === 0}
                />
              </div>
            )}

            {/* Notes Section */}
            {groupedNotes.notes.length > 0 && (
              <div
                ref={(ref) => {
                  if (ref) {
                    noteSectionRefs.current.set("Notes", ref);
                  }
                }}
                className="scroll-mt-52"
              >
                <PublicView
                  allBusinessNotes={groupedNotes.notes}
                  onRefresh={onRefresh}
                  onEdit={handleEditNote}
                  onFlagNote={handleFlagNote}
                  showMapperView={showMapperView}
                  businessNotesActionsVenueNotes={
                    venueDetailsResponse.details
                      .businessNotesActionsVenueNotes
                  }
                  businessNotesActions={
                    venueDetailsResponse.details.businessNotesActions
                  }
                  businessId={resolveBusinessId}
                  placeName={resolvePlaceName}
                  brandId={resolveBrandId}
                  brandName={resolveBrandName}
                  isFirstSection={(!showMapperView || groupedNotes.mapper.length === 0) && groupedNotes.liveAlerts.length === 0}
                />
              </div>
            )}

            {/* Empty state */}
            {sortedAllNotes.length === 0 && (
              <div className="flex flex-col justify-center items-center py-12">
                <p className="text-center title-s-semibold text-fg-neutral-strong">
                  {i18n.get("noAlertsOrNotesYet")}
                </p>
                <p className="text-center label-m-medium mt-3 text-fg-neutral-subtle">
                  Be the first to add information about this venue
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Select Add Note Type Modal - Available regardless of notes count */}
      {addNoteTypeOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addNoteTypeOpenModal}
          onClose={() => setAddNoteTypeOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <SelectAddNoteType
            onClose={() => {
              createPageAnalytics("add_note_menu").track("close_action_button", {
                brand_id: venueDetailsResponse?.details?.brandId ?? "",
                brand_name: venueDetailsResponse?.details?.brandName ?? "",
                business_id:
                  venueDetailsResponse?.details?.business?.id?.toString() ?? "",
                place_name: venueDetailsResponse?.details?.name ?? "",
              });
              setAddNoteTypeOpenModal(false);
            }}
            onMapperNoteClick={() => {
              setAddNoteTypeOpenModal(false);
              setAddNoteOpenModal(true);
            }}
            onVenueNoteClick={() => {
              setAddNoteTypeOpenModal(false);
              setAddVenueNoteOpenModal(true);
            }}
          />
        </Modal>
      )}

      {/* Area Details Notes Modal */}
      {areaDetailsOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={areaDetailsOpenModal}
          onClose={() => setAreaDetailsOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AreaDetailsNotes
            venueDetailsResponse={venueDetailsResponse}
            mode={addNoteOpenModal ? "mappernote" : "venuenote"}
            onClose={() => {
              setAreaDetailsOpenModal(false);
            }}
          />
        </Modal>
      )}

      {/* Add Mapper Note Modal */}
      {addNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addNoteOpenModal}
          onClose={() => setAddNoteOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AddMapperNote
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => {
              setAddNoteOpenModal(false);
              onRefresh();
            }}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
          />
        </Modal>
      )}

      {/* Add Venue Note Modal */}
      {addVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addVenueNoteOpenModal}
          onClose={() => setAddVenueNoteOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AddVenueNote
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => {
              setAddVenueNoteOpenModal(false);
              onRefresh();
            }}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
          />
        </Modal>
      )}

      {/* Edit Venue Note Modal */}
      {editVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={editVenueNoteOpenModal}
          onClose={() => setEditVenueNoteOpenModal(false)}
          closeOnOverlayClick={false}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <EditVenueNote
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => {
              setEditVenueNoteOpenModal(false);
              onRefresh();
            }}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
            note={editNote}
          />
        </Modal>
      )}

      {/* Flag Venue Note Modal */}
      {flagVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={flagVenueNoteOpenModal}
          onClose={() => setFlagVenueNoteOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <FlagVenueNote
            venueDetails={venueDetailsResponse.details}
            onClose={() => {
              setFlagVenueNoteOpenModal(false);
              onRefresh();
            }}
            note={flagNote}
          />
        </Modal>
      )}
    </>
  );
};

export default VenueMapperNotes;
